import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { HottieMessage as KitHottieMessage } from '@/kit';
import { env } from '@/config/env';
import { getPassportAccessToken } from '@/auth/passport';
import { getSupabase } from '@/lib/supabase';
import { peek } from '@/modules/heat/signals';
import { hottieChatAuthHeaders } from './mateApi';
import { registerHottieChatSend } from './hottieChatSend';
import { respond } from './prompt';
import type { HottieMessage, ToolCall } from './types';

const SEED: HottieMessage = {
  id: 'm0',
  from: 'hottie',
  text: "hey. what's the play tonight.",
  at: Date.now(),
};

const toKit = (row: HottieMessage): KitHottieMessage => ({
  id: row.id,
  speaker: row.from === 'user' ? 'you' : 'hottie',
  text: row.text,
});

/**
 * Fills the in-kit HottieSheet slots. Not a second dock — OrbFan/OrbRail
 * already own the chrome. This only talks to the model.
 *
 * Voice Stack audio chrome stays out of this hook (separate lane).
 */
export function useHottieChat() {
  const navigate = useNavigate();
  const [messages, setMessages] = useState<HottieMessage[]>([SEED]);
  const [loading, setLoading] = useState(false);
  const loadingRef = useRef(false);
  loadingRef.current = loading;

  const handleToolCalls = (toolCalls: ToolCall[]) => {
    for (const call of toolCalls) {
      if (call.function.name === 'navigate') {
        try {
          const args = JSON.parse(call.function.arguments) as { route: string };
          navigate(args.route);
        } catch {
          /* malformed args — skip */
        }
      }
    }
  };

  const send = async (text: string): Promise<string | undefined> => {
    if (!text.trim() || loadingRef.current) return undefined;
    const at = Date.now();
    setMessages((prev) => [...prev, { id: `u-${at}`, from: 'user', text, at }]);
    setLoading(true);

    const supabase = getSupabase();
    if (!supabase) {
      const reply = respond(text);
      setMessages((prev) => [
        ...prev,
        { id: `h-${at}`, from: 'hottie', text: reply, at: Date.now() },
      ]);
      setLoading(false);
      return reply;
    }

    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) throw new Error('not authenticated');

      const passportToken = await getPassportAccessToken();
      const res = await fetch(`${env.supabaseUrl}/functions/v1/hottie-chat`, {
        method: 'POST',
        headers: hottieChatAuthHeaders({
          destAccessToken: session.access_token,
          passportAccessToken: passportToken,
          anonKey: env.supabaseAnonKey,
        }),
        body: JSON.stringify({ message: text, heat_snapshot: peek() }),
      });

      if (!res.ok) {
        const errBody = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(errBody.error || `HTTP ${res.status}`);
      }

      const data = (await res.json()) as {
        content: string;
        tool_calls: ToolCall[];
      };

      setMessages((prev) => [
        ...prev,
        { id: `h-${Date.now()}`, from: 'hottie', text: data.content, at: Date.now() },
      ]);
      if (data.tool_calls?.length > 0) handleToolCalls(data.tool_calls);
      return data.content;
    } catch (err) {
      console.error('[Hottie]', err);
      const reply = err instanceof Error ? err.message : 'something went wrong. try again?';
      setMessages((prev) => [
        ...prev,
        {
          id: `h-${Date.now()}`,
          from: 'hottie',
          text: reply,
          at: Date.now(),
        },
      ]);
      return reply;
    } finally {
      setLoading(false);
    }
  };

  const sendRef = useRef(send);
  sendRef.current = send;

  useEffect(() => {
    registerHottieChatSend((text) => sendRef.current(text));
    return () => registerHottieChatSend(null);
  }, []);

  return { messages: messages.map(toKit), send };
}
